import { useState, MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MuiAppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Container from '@mui/material/Container';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import UserAvatar from '../userAvatar/userAvatar.';
import { useUserContext } from '@/contexts/userContext';
import DrawerPanel from '../drawerPanel';
import UserProfile from '../userProfile';
import ExploreIcon from '@mui/icons-material/Explore';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LayersIcon from '@mui/icons-material/Layers';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LoginIcon from '@mui/icons-material/Login';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ScienceIcon from '@mui/icons-material/Science';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import DomainSwitcher from '../domain/DomainSwitcher';

function AppBar() {
  const userContext = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [showProfile, setShowProfile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const logout = async () => {
    await userContext.signout();
    handleCloseUserMenu();
  };

  const viewProfile = () => {
    setShowProfile(true);
    handleCloseUserMenu();
  };

  const hideProfile = () => {
    setShowProfile(false);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', p: 2 }}>
      <Typography variant="h6" sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
        <img src="/brand/minotii-logo-color.svg" width={100} />
      </Typography>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
        <DomainSwitcher />
      </Box>
      <Divider sx={{ mb: 2 }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/marketplace')} sx={{ borderRadius: '8px', mx: 1 }}>
            <ListItemIcon>
              <ExploreIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Marketplace" primaryTypographyProps={{ fontWeight: 600 }} />
          </ListItemButton>
        </ListItem>

        {userContext?.user ? (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ px: 2, py: 1, textAlign: 'left' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {userContext.user.firstName} {userContext.user.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {userContext.user.email}
              </Typography>
            </Box>
            <Divider sx={{ my: 1.5 }} />

            <ListItem disablePadding>
              <ListItemButton
                onClick={() => navigate('/dashboard')}
                selected={location.pathname === '/dashboard'}
                sx={{ borderRadius: '8px', mx: 1 }}
              >
                <ListItemIcon>
                  <DashboardIcon color={location.pathname === '/dashboard' ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText
                  primary="Dashboard"
                  primaryTypographyProps={{
                    fontWeight: location.pathname === '/dashboard' ? 700 : 600,
                    color: location.pathname === '/dashboard' ? 'primary.main' : 'text.primary',
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                onClick={() => navigate('/instances')}
                selected={location.pathname.startsWith('/instances')}
                sx={{ borderRadius: '8px', mx: 1 }}
              >
                <ListItemIcon>
                  <LayersIcon color={location.pathname.startsWith('/instances') ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText
                  primary="Instances"
                  primaryTypographyProps={{
                    fontWeight: location.pathname.startsWith('/instances') ? 700 : 600,
                    color: location.pathname.startsWith('/instances') ? 'primary.main' : 'text.primary',
                  }}
                />
              </ListItemButton>
            </ListItem>

            {userContext.hasPermission('pipeline:view') && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => navigate('/administration')}
                  selected={location.pathname === '/administration'}
                  sx={{ borderRadius: '8px', mx: 1 }}
                >
                  <ListItemIcon>
                    <SettingsIcon color={location.pathname === '/administration' ? 'primary' : 'inherit'} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Administration"
                    primaryTypographyProps={{
                      fontWeight: location.pathname === '/administration' ? 700 : 600,
                      color: location.pathname === '/administration' ? 'primary.main' : 'text.primary',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}

            <ListItem disablePadding>
              <ListItemButton
                onClick={() => navigate('/assistant')}
                selected={location.pathname === '/assistant'}
                sx={{ borderRadius: '8px', mx: 1 }}
              >
                <ListItemIcon>
                  <SmartToyIcon color={location.pathname === '/assistant' ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText
                  primary="Assistant"
                  primaryTypographyProps={{
                    fontWeight: location.pathname === '/assistant' ? 700 : 600,
                    color: location.pathname === '/assistant' ? 'primary.main' : 'text.primary',
                  }}
                />
              </ListItemButton>
            </ListItem>

            <Divider sx={{ my: 1.5 }} />

            <ListItem disablePadding>
              <ListItemButton onClick={viewProfile} sx={{ borderRadius: '8px', mx: 1 }}>
                <ListItemIcon>
                  <AccountCircleIcon />
                </ListItemIcon>
                <ListItemText primary="Profile" primaryTypographyProps={{ fontWeight: 600 }} />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton onClick={logout} sx={{ borderRadius: '8px', mx: 1 }}>
                <ListItemIcon>
                  <LogoutIcon color="error" />
                </ListItemIcon>
                <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 600, color: 'error.main' }} />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <>
            <Divider sx={{ my: 1.5 }} />
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/login')} sx={{ borderRadius: '8px', mx: 1 }}>
                <ListItemIcon>
                  <LoginIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Login" primaryTypographyProps={{ fontWeight: 700 }} />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <MuiAppBar position="static" elevation={0} sx={{ bgcolor: 'white' }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { md: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            component="a"
            onClick={() => navigate('/')}
            sx={{
              mr: 2,
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            <img src="/brand/minotii-logo-color.svg" width={120} />
          </Typography>

          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', ml: 0.5, mr: 1.5 }}>
            <DomainSwitcher />
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2, ml: 1 }}>
            <Button
              onClick={() => navigate('/marketplace')}
              startIcon={<ExploreIcon sx={{ fontSize: '18px' }} />}
              sx={{
                fontWeight: 750,
                textTransform: 'none',
                bgcolor: 'rgba(124, 58, 237, 0.06)',
                px: 2,
                py: 0.75,
                borderRadius: '20px',
                border: '1px solid rgba(124, 58, 237, 0.15)',
                color: 'primary.main',
                fontSize: '13.5px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(124, 58, 237, 0.12)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Explore Marketplace
            </Button>
            <Button
              onClick={async () => {
                let url = 'https://www.biopharm.minotii.ai/';
                try {
                  const token = await userContext?.getAccessToken?.();
                  if (token) {
                    url += `?token=${encodeURIComponent(token)}`;
                  }
                } catch (e) {
                  // ignore
                }
                window.open(url, '_blank');
              }}
              startIcon={<ScienceIcon sx={{ fontSize: '18px', color: '#10B981' }} />}
              sx={{
                fontWeight: 750,
                textTransform: 'none',
                bgcolor: 'rgba(16, 185, 129, 0.08)',
                px: 2,
                py: 0.75,
                borderRadius: '20px',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                color: '#059669',
                fontSize: '13.5px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(16, 185, 129, 0.15)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              BioPharm Portal
            </Button>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {!userContext?.user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => navigate('/login')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '8px',
                  px: 2.5,
                }}
              >
                Login
              </Button>
            </Box>
          )}

          {userContext?.user && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, mr: 2 }}>
              <Button
                onClick={() => navigate('/dashboard')}
                startIcon={<DashboardIcon sx={{ fontSize: '17px' }} />}
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '13.5px',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '10px',
                  transition: 'all 0.2s ease',
                  color: location.pathname === '/dashboard' ? 'primary.main' : 'text.secondary',
                  bgcolor: location.pathname === '/dashboard' ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  '&:hover': {
                    bgcolor:
                      location.pathname === '/dashboard'
                        ? alpha(theme.palette.primary.main, 0.12)
                        : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                Dashboard
              </Button>
              <Button
                onClick={() => navigate('/instances')}
                startIcon={<LayersIcon sx={{ fontSize: '17px' }} />}
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '13.5px',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '10px',
                  transition: 'all 0.2s ease',
                  color: location.pathname.startsWith('/instances') ? 'primary.main' : 'text.secondary',
                  bgcolor: location.pathname.startsWith('/instances')
                    ? alpha(theme.palette.primary.main, 0.08)
                    : 'transparent',
                  '&:hover': {
                    bgcolor: location.pathname.startsWith('/instances')
                      ? alpha(theme.palette.primary.main, 0.12)
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                Instances
              </Button>
              {userContext.hasPermission('pipeline:view') && (
                <Button
                  onClick={() => navigate('/administration')}
                  startIcon={<SettingsIcon sx={{ fontSize: '17px' }} />}
                  sx={{
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '13.5px',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '10px',
                    transition: 'all 0.2s ease',
                    color: location.pathname === '/administration' ? 'primary.main' : 'text.secondary',
                    bgcolor:
                      location.pathname === '/administration' ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    '&:hover': {
                      bgcolor:
                        location.pathname === '/administration'
                          ? alpha(theme.palette.primary.main, 0.12)
                          : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  Administration
                </Button>
              )}
              <Button
                onClick={() => navigate('/assistant')}
                startIcon={<SmartToyIcon sx={{ fontSize: '17px' }} />}
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '13.5px',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '10px',
                  transition: 'all 0.2s ease',
                  color: location.pathname === '/assistant' ? 'primary.main' : 'text.secondary',
                  bgcolor: location.pathname === '/assistant' ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  '&:hover': {
                    bgcolor:
                      location.pathname === '/assistant'
                        ? alpha(theme.palette.primary.main, 0.12)
                        : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                Assistant
              </Button>
            </Box>
          )}

          {userContext?.user && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', mr: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {userContext?.user.firstName} {userContext?.user.lastName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {userContext?.user.email}
              </Typography>
            </Box>
          )}

          {userContext?.user && (
            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <UserAvatar username={`${userContext?.user.firstName} ${userContext?.user.lastName}`} />
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem onClick={viewProfile}>
                  <Typography textAlign="center">Profile</Typography>
                </MenuItem>
                <MenuItem onClick={logout}>
                  <Typography textAlign="center">Logout</Typography>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </Container>
      <DrawerPanel open={showProfile} onClose={hideProfile} minWidth={'40vw'}>
        <UserProfile onClose={hideProfile} />
      </DrawerPanel>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </MuiAppBar>
  );
}
export default AppBar;
